import React, { useRef, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate, Link } from 'react-router-dom';
import './App.css';
import AppletWidget from './components/AppletWidget';
import Projects from './components/Projects';
import FlyingDots from './components/FlyingDots';
import AnimatedLogo from './components/AnimatedLogo';
import CozyBackground from './components/CozyBackground';
import NetworkStatus from './components/NetworkStatus';
import ThemeToggle from './components/ThemeToggle';
import { NotificationProvider } from './contexts/NotificationContext';

const appText = {
  aboutUs: 'About Us',
  projects: 'Projects',
  whatIsAI: 'What is AI Driven Labs',
  howItWorks: 'How it works',
  team: 'Team',
  contactUs: 'Contact us',
  copyright: '© 2026 AI Driven Labs',
  whatIsAIDesc2: 'We make artificial intelligence accessible to everyone.',
  whatIsAIDescPartners: 'AI Driven Labs is also a cutting-edge platform for partners to easily deploy AI agents to do the job.',
  keyFeatures: 'Key Features',
  easyCreation: 'Easy Creation',
  easyCreationDesc: 'Build AI conversation agents in minutes using our user-friendly interface',
  customizableProfiles: 'Customizable Profiles',
  customizableProfilesDesc: 'Create multiple AI profiles with custom prompts, document attachments, and model selection',
  topAIModels: 'Top AI Models',
  topAIModelsDesc: 'Access leading HuggingFace models including Llama 3.1, Mistral, and Mixtral',
  simpleDeployment: 'Simple Deployment',
  simpleDeploymentDesc: 'Generate HTML embed codes (Applets) that integrate seamlessly into any website',
  conversationAnalytics: 'Conversation Analytics',
  conversationAnalyticsDesc: 'Monitor and analyze interactions from your deployed AI agents',
  privacyFirst: 'Privacy-First',
  privacyFirstDesc: 'All data is stored securely with Firebase, ensuring your AI configurations remain private and protected',
  whatIsAIDesc3: 'AI Driven Labs bridges the gap between advanced AI technology and practical, everyday applications. No coding skills required, no complex configurations—just create, customize, and deploy your AI agents wherever you need them.',
  gettingStarted: 'Getting Started',
  gettingStartedDesc: 'Getting started with AI Driven Labs is simple. Head over to the Aidrivex platform, create an account or log in with your existing credentials. Once you\'re in, you can immediately begin creating your first AI conversation agent.',
  ourProjects: 'Our Projects',
  ourProjectsDesc: 'The Projects page is a public showcase of products built by the AI Driven Labs team — real-world applications powered by the same AI conversation technology you can deploy yourself. Browse the projects to see what is possible, get inspiration for your own use cases, and discover the kinds of experiences you can create. Projects are open to everyone, no login required.',
  createYourAI: 'Create Your AI Agent',
  createYourAIDesc: 'On the Aidrivex platform, click on "Add AI" to create a new Conversation AI agent. Your first agent is automatically created with a default profile, ready for customization. You can create multiple agents for different purposes, each with its own unique configuration.',
  customizeAIProfile: 'Customize Your AI Profile',
  customizeAIProfileDesc: 'Navigate to "Edit AI" to customize your agent\'s behavior and capabilities:',
  setPrompt: 'Set the Prompt',
  setPromptDesc: 'Define how your AI should behave, what knowledge it should have, and how it should respond using our step-by-step wizard',
  uploadDocuments: 'Upload Documents',
  uploadDocumentsDesc: 'Attach documents that contain knowledge your AI should reference and learn from',
  selectModel: 'Select Model',
  selectModelDesc: 'Choose from top-performing AI models. We recommend Llama 3.1 8B for most use cases',
  configureParameters: 'Configure Parameters',
  configureParametersDesc: 'Fine-tune responses by adjusting temperature, max tokens, and top P values',
  deployYourAI: 'Deploy Your AI',
  deployYourAIDesc: 'Once your AI profile is ready, generate an HTML embed code (Applet) with a single click. Copy the generated code and paste it into any website where you want your AI agent to appear. The agent will instantly become available as an interactive chat widget for your visitors.',
  monitorConversations: 'Monitor Conversations',
  monitorConversationsDesc: 'Use "Check AI" to view all conversations that users have had with your deployed AI agents. Analyze interactions, understand user needs, identify common questions, and continuously improve your AI\'s performance based on real-world usage data.',
  technicalArchitecture: 'Technical Architecture',
  technicalArchitectureDesc: 'AI Driven Labs is built on a robust, modern technology stack:',
  frontend: 'Frontend',
  frontendDesc: 'React-based user interface providing a responsive and intuitive experience',
  backendAPI: 'Backend API',
  backendAPIDesc: 'Express.js server handling AI inference requests using state-of-the-art HuggingFace models',
  database: 'Database',
  databaseDesc: 'Firebase Firestore securely stores all your AI configurations, profiles, and conversation logs',
  storage: 'Storage',
  storageDesc: 'Firebase Storage manages document uploads for your AI knowledge base',
  authentication: 'Authentication',
  authenticationDesc: 'Firebase Authentication ensures secure user account management',
  teamTitle: 'AI Driven Labs Team',
  ourMission: 'Our Mission',
  ourMissionDesc: 'Making AI accessible to everyone',
  teamDesc1: 'We are a passionate team of developers and AI enthusiasts dedicated to democratizing artificial intelligence technology. Our mission is to empower individuals and businesses to harness the power of conversational AI without the complexity and barriers that traditionally come with AI development.',
  teamDesc2: 'We believe that AI should be accessible, customizable, and easy to deploy. Every person and organization, regardless of their technical background, should be able to leverage the transformative power of artificial intelligence to solve their unique challenges.',
  teamDesc3: 'AI Driven Labs is built by developers who understand both the incredible potential and the inherent complexity of AI. We\'ve created a platform that bridges the gap between cutting-edge AI technology and practical, real-world applications. Our team is committed to continuously improving the platform, adding new features, and ensuring the best possible experience for our users.',
  teamDesc4: 'Join us on our journey to make AI technology accessible, powerful, and transformative for everyone.',
  contactDesc1: 'Have questions, feedback, or want to learn more about AI Driven Labs? We\'d love to hear from you! Our team is here to help you get the most out of our platform.',
  emailLabel: 'Email:',
  contactDesc2: 'Whether you\'re looking for technical support, have feature requests, want to discuss how AI Driven Labs can help your business, or simply want to share your feedback, we\'re here to help. We value your input and are committed to making AI Driven Labs the best platform it can be.',
  contactDesc3: 'We typically respond to all inquiries within 24-48 hours. For urgent matters, please include "URGENT" in your subject line.'
};

const APP_VERSION = '0.101';

function App() {
  return (
    <NotificationProvider>
      <Router>
        <Routes>
          <Route path="/applet/:appletId" element={<AppletPage />} />
          <Route path="/about/*" element={<MainSite />} />
          <Route path="/projects" element={<MainSite />} />
          <Route path="*" element={<MainSite />} />
        </Routes>
      </Router>
    </NotificationProvider>
  );
}

function AppletPage() {
  const location = useLocation();
  const appletId = location.pathname.split('/applet/')[1];
  return (
    <div style={{ width: '100%', height: '100vh', padding: '20px', boxSizing: 'border-box' }}>
      <AppletWidget appletId={appletId} />
    </div>
  );
}

function MainSite() {
  const location = useLocation();
  const navigate = useNavigate();
  const [aboutSection, setAboutSection] = useState('what-is-ai-driven-labs');
  const [newVersionAvailable, setNewVersionAvailable] = useState(false);
  const [serverVersion, setServerVersion] = useState('');
  const aboutSectionRef = useRef(null);
  const t = appText;

  useEffect(() => {
    let cancelled = false;
    const checkVersion = async () => {
      try {
        const res = await fetch('/version.json?t=' + Date.now(), { cache: 'no-store' });
        if (res.ok) {
          const data = await res.json();
          const cleanServerVer = String(data.version || '').replace(/^v/, '').trim();
          if (!cancelled && cleanServerVer && cleanServerVer !== APP_VERSION) {
            setServerVersion(cleanServerVer);
            setNewVersionAvailable(true);
          }
        }
      } catch {}
    };

    checkVersion();
    const interval = setInterval(checkVersion, 4500);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const path = location.pathname;
  const aboutVisible = path.startsWith('/about');
  const projectsVisible = path.startsWith('/projects');

  // Default route: redirect to /projects
  useEffect(() => {
    if (path === '/' || path === '') {
      navigate('/projects', { replace: true });
    }
  }, [path, navigate]);

  useEffect(() => {
    const measure = () => {
      const mc = document.querySelector('.main-content');
      if (!mc) return;
      const sw = mc.offsetWidth - mc.clientWidth;
      document.documentElement.style.setProperty('--scrollbar-width', sw + 'px');
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  });

  // Sync About section from URL
  useEffect(() => {
    if (!path.startsWith('/about')) return;
    const sectionPath = path.replace('/about/', '').replace('/about', '') || 'what-is-ai-driven-labs';
    const sectionMap = {
      'what-is-ai-driven-labs': 'what-is-ai-driven-labs',
      'team': 'team',
      'contact-us': 'contact-us'
    };
    setAboutSection(sectionMap[sectionPath] || 'what-is-ai-driven-labs');
    if (path === '/about') {
      navigate('/about/what-is-ai-driven-labs', { replace: true });
    }
  }, [path, navigate]);

  const aboutContent = (
    <div className="about-section" ref={aboutSectionRef}>
      <div className="about-content">
        <div className="about-nav">
          <a
            href="#what-is-ai-driven-labs"
            className={`about-nav-link ${aboutSection === 'what-is-ai-driven-labs' ? 'active' : ''}`}
            onClick={(e) => {
              e.preventDefault();
              navigate('/about/what-is-ai-driven-labs');
              if (aboutSectionRef.current) aboutSectionRef.current.scrollTop = 0;
            }}
          >
            {t.whatIsAI}
          </a>
          <a
            href="#team"
            className={`about-nav-link ${aboutSection === 'team' ? 'active' : ''}`}
            onClick={(e) => {
              e.preventDefault();
              navigate('/about/team');
              if (aboutSectionRef.current) aboutSectionRef.current.scrollTop = 0;
            }}
          >
            {t.team}
          </a>
          <a
            href="#contact-us"
            className={`about-nav-link ${aboutSection === 'contact-us' ? 'active' : ''}`}
            onClick={(e) => {
              e.preventDefault();
              navigate('/about/contact-us');
              if (aboutSectionRef.current) aboutSectionRef.current.scrollTop = 0;
            }}
          >
            {t.contactUs}
          </a>
        </div>

        {aboutSection === 'what-is-ai-driven-labs' && (
          <div id="what-is-ai-driven-labs" className="about-section-wrapper">
            <h1>{t.whatIsAI}</h1>
            <div className="team-content">
              <div className="team-card-wrapper">
                <div className="team-logo-card"><img src="/logo.svg" alt="AI Driven Labs" /></div>
                <div className="team-info">
                  <h2>{t.teamTitle}</h2>
                  <p className="team-mission"><strong>{t.ourMission}:</strong> {t.ourMissionDesc}</p>
                  <p>
                    <strong>AI Driven Labs is a team of dreamers.</strong> We are a passionate group of developers and AI enthusiasts dedicated to democratizing AI and Web3 technology by providing intuitive tools that allow users to harness the power of conversational AI and Blockchain for their specific needs. Whether you're looking to enhance customer support, create interactive website assistants, or build specialized AI agents for your business, AI Driven Labs provides the solution.
                  </p>
                  <p>
                    We empower individuals and businesses to create, customize, and deploy AI agents without requiring any technical expertise. We believe that AI should be accessible, customizable, and easy to deploy — every person and organization, regardless of their technical background, should be able to leverage the transformative power of artificial intelligence to solve their unique challenges.
                  </p>
                  <p>
                    AI Driven Labs is built by developers who understand both the incredible potential and the inherent complexity of AI. We've created a platform that bridges the gap between cutting-edge AI technology and practical, real-world applications — and it's also a cutting-edge platform for partners to easily deploy AI agents to do the job. Our team is committed to continuously improving the platform, adding new features, and ensuring the best possible experience for our users.
                  </p>
                  <p>
                    <strong>We make artificial intelligence accessible to everyone.</strong> Join us on our journey to make AI technology accessible, powerful, and transformative for everyone.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {aboutSection === 'team' && (
          <div id="team" className="about-section-wrapper">
            <h1>{t.team}</h1>
            <div className="team-content">
              <div className="team-card-wrapper team-member">
                <div className="team-logo-card team-photo-card">
                  <img src="/alex.png" alt="Alex" />
                </div>
                <div className="team-info">
                  <h2>Alex</h2>
                  <p>Founder & builder at AI Driven Labs. Loves shipping small, sharp tools that turn complex AI and Web3 ideas into things people actually use.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {aboutSection === 'contact-us' && (
          <div id="contact-us" className="about-section-wrapper">
            <h1>{t.contactUs}</h1>
            <p>{t.contactDesc1}</p>
            <p className="contact-email-wrapper">
              <strong>{t.emailLabel}</strong> <a href="mailto:aleksey.laptev@gmail.com" className="contact-email">aleksey.laptev@gmail.com</a>
            </p>
            <p>{t.contactDesc2}</p>
            <p>{t.contactDesc3}</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="App">
      <CozyBackground />
      <header className="app-header">
        <FlyingDots />
        <div className="header-row">
          <Link to="/projects" className="header-left" style={{ textDecoration: 'none', color: 'inherit' }}>
            <div className="logo-icon"><AnimatedLogo /></div>
            <div className="logo-text">
              <span className="logo-ai">AI</span> <span className="logo-driven">Driven</span> <span className="logo-labs">Labs</span>
            </div>
            <span id="appVersion" className="version-badge">v{APP_VERSION}</span>
          </Link>
          <nav className="header-nav">
            <button
              className={`nav-link ${aboutVisible ? 'active' : ''}`}
              onClick={() => navigate('/about')}
            >
              {t.aboutUs}
            </button>
            <button
              className={`nav-link ${projectsVisible ? 'active' : ''}`}
              onClick={() => navigate('/projects')}
            >
              {t.projects}
            </button>
            <ThemeToggle />
          </nav>
        </div>
      </header>

      <main className="main-content about-main-content">
        {aboutVisible ? aboutContent : <Projects />}
      </main>

      <footer className="app-footer">
        <div className="footer-bottom">
          <div className="footer-copyright">{t.copyright}</div>
          <NetworkStatus />
        </div>
      </footer>

      <div id="newVersionBanner" className="new-version-banner" style={{ display: newVersionAvailable ? 'block' : 'none' }}>
        <div className="new-version-content">
          <span className="new-version-icon">🚀</span>
          <span className="new-version-text">
            New version available (<span id="newVersionNumber">v{serverVersion || '0.000'}</span>)
          </span>
          <button id="reloadAppBtn" className="btn-reload" onClick={() => window.location.reload(true)}>
            🔄 Reload
          </button>
        </div>
      </div>
    </div>
  );
}

export default App;
